import {
    DatapointStatus,
    lazyLogSigsDatapoint,
    logUsage,
    PerformanceDatapoint,
} from 'owa-analytics';
import { assertNever } from 'owa-assert';
import {
    AttachmentFullViewState,
    getSaveToCloudEstimatedCompleteTimeInMS,
    saveToCloud,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
    trackSaveToCloud,
} from 'owa-attachment-full-data';
import AddToCalendarActionSource from 'owa-attachment-full-view/lib/types/ActionSource';
import { addToCalendar } from 'owa-attachment-full-view/lib/utils/ContextMenu/addCalendarEventsAttachmentContextItemBuilder';
import { isSmimeAttachmentType } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { lazyPreviewAttachment, PreviewContext } from 'owa-attachment-select-actions';
import { downloadAttachmentFromUrl } from 'owa-attachment-user-operations';
import { ClientItemId } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { lazyDownloadSmimeAttachment } from 'owa-smime';
import { PreviewPaneSelectionSource } from 'owa-sxs-store';
import * as trace from 'owa-trace';
import { openNewTabAndSave } from './openNewTabAndSave';
import { SurfaceAction } from '../../types/SurfaceAction';
import getSenderFromItemId from '../../utils/getSenderFromItemId';

export function performSurfaceAction(
    action: SurfaceAction,
    viewState: AttachmentFullViewState,
    parentItemId: ClientItemId
): void {
    switch (action) {
        case SurfaceAction.addToCalendar:
            addToCalendar(viewState, AddToCalendarActionSource.SurfaceAction);
            break;
        case SurfaceAction.download:
            downloadAttachment(viewState, parentItemId);
            break;
        case SurfaceAction.saveToCloud:
            saveToCloudInternal(viewState, parentItemId);
            break;
        case SurfaceAction.editAndReply:
            editAndReplyFromSurface(viewState, parentItemId);
            break;
        case SurfaceAction.openNewTabAndSaveToCloud:
            saveInNewTab(viewState, parentItemId);
            break;
        default:
            assertNever(action);
    }
}

async function saveInNewTab(viewState: AttachmentFullViewState, parentItemId: ClientItemId) {
    const subscriptionId = getGuid();
    openNewTabAndSave(viewState.attachmentId.Id, subscriptionId, parentItemId);

    const datapoint = new PerformanceDatapoint('WovenNewTabSaveToCloud', { isCore: true });

    startFakeProgressIndicator(
        getSaveToCloudEstimatedCompleteTimeInMS(viewState.attachmentId) * 4,
        0.1,
        (completePercent: number) => {
            setActionCompletePercent(viewState, completePercent);
        },
        viewState.attachmentId
    );
    trackSaveToCloud(
        viewState,
        (succeeded: boolean) => {
            stopFakeProgressIndicator(viewState.attachmentId, succeeded);
            if (succeeded) {
                datapoint.end();
            } else {
                datapoint.endWithError(DatapointStatus.ServerError);
            }
        },
        'PencilExperiment',
        parentItemId,
        subscriptionId,
        getSenderFromItemId(parentItemId?.Id)
    );
}

async function editAndReplyFromSurface(viewState, parentItemId) {
    const previewAttachment = await lazyPreviewAttachment.import();
    logUsage('WovenEditAndReplyFromSurface', {}, { isCore: true });
    const context: PreviewContext = {
        viewState,
        previewPaneSelectionSource: PreviewPaneSelectionSource.ReadingPane,
        parentItemId,
        isDraft: false,
        isEdit: true,
        onBeforePreview: () => null,
        createImageGallery: () => null,
        instrumentationContext: null,
    };

    previewAttachment(context);
    return;
}

// Note: we use document.body as the container element since inside of downloadAttachment it creates a container element that
// is either the parent of the active element, or document.body.
// The parent of the active element is inside the attachmentFullViewState, so the logic in downloadAttachment
// mocks a click on the attachment in this case, which triggers opening SxS/a second download, based on the type of attachment/permissions.
async function downloadAttachment(viewState: AttachmentFullViewState, parentItemId: ClientItemId) {
    const attachmentModel = getAttachment(viewState.attachmentId);
    if (!attachmentModel) {
        trace.errorThatWillCauseAlert(
            'AttachmentSurfaceActionButton.downloadAttachment attachmentModel is null'
        );
        return;
    }
    const fileType = getAttachmentFileType(attachmentModel);
    logUsage(
        'AttachmentDownload',
        {
            entryPoint: 'SurfaceAction',
            isCloudy: viewState.isCloudy,
            isCompose: !viewState.isReadOnly,
        },
        {
            cosmosOnlyData: JSON.stringify({
                size: attachmentModel.model.Size,
                fileType,
                attachmentId: attachmentModel.model.AttachmentId?.Id,
            }),
        }
    );
    if (isFeatureEnabled('doc-attachment-sigs')) {
        const itemId = parentItemId?.Id;
        lazyLogSigsDatapoint.importAndExecute('SavedAttachment', {
            itemId,
            customProperties: {
                FileId: attachmentModel.model.AttachmentId?.Id,
                FileType: fileType,
                Destination: 'Local',
                Sender: getSenderFromItemId(itemId),
            },
        });
    }

    if (isSmimeAttachmentType(attachmentModel.model)) {
        (await lazyDownloadSmimeAttachment.import())(attachmentModel.model);
    } else {
        downloadAttachmentFromUrl(
            attachmentModel.download.url,
            viewState.isCloudy,
            document.body /* containerElement*/
        );
    }
}

function saveToCloudInternal(viewState: AttachmentFullViewState, parentItemId: ClientItemId) {
    const attachmentModel = getAttachment(viewState.attachmentId);
    if (!attachmentModel) {
        trace.errorThatWillCauseAlert(
            'AttachmentSurfaceActionButton.saveToCloud attachmentModel is null'
        );
        return;
    }
    logUsage(
        'AttachmentSaveToCloud',
        {
            entryPoint: 'SurfaceAction',
        },
        {
            cosmosOnlyData: JSON.stringify({
                size: attachmentModel.model.Size,
                fileType: getAttachmentFileType(attachmentModel),
                attachmentId: attachmentModel.model.AttachmentId?.Id,
            }),
            isCore: true,
        }
    );

    startFakeProgressIndicator(
        getSaveToCloudEstimatedCompleteTimeInMS(viewState.attachmentId),
        0.1,
        (completePercent: number) => {
            setActionCompletePercent(viewState, completePercent);
        },
        viewState.attachmentId
    );
    saveToCloud(
        viewState,
        (succeeded: boolean) => {
            stopFakeProgressIndicator(viewState.attachmentId, succeeded);
        },
        'SurfaceAction',
        parentItemId,
        getSenderFromItemId(parentItemId?.Id)
    );
}
